import { supabase } from "./supabase";
import { erEkteProduksjon, hentOekt, settOekt } from "./session";
import type { Session } from "./types";

/**
 * Stillas: la telefonen vaere en annen paa produksjonen.
 *
 * ========  HVORFOR DETTE FINNES  ========
 *
 * Teamet deler logg fra 29.10.2026, og den flyten kan bare proeves med TO
 * ENHETER SOM ER TO ULIKE MEDLEMMER PAA SAMME TEAM. Den krever ikke
 * innlogging — den krever bare at de to er ulike.
 *
 * Og innlogging er blokkert paa noe annet: e-postmalen kan ikke settes uten
 * egen SMTP, saa det finnes én konto i prosjektet. Uten dette stillaset
 * ville den foerste toveisflyten i appen staatt uproevd til den
 * blokkeringen er loest.
 *
 * ALT ANNET ER EKTE. Den utregnede oekt-id-en, hentingen av de andres
 * poster, ankrene som foelger med, rapportsperren — og den ekte basen.
 * Det eneste som er paafunnet er hvem telefonen sier at den er.
 *
 * ========  DEN FORSVINNER AV SEG SELV  ========
 *
 * Rendres bare naar `krevInnlogging` er av. Med auth paa sier
 * `production_member` hvem du er, og basen haandhever det — da ville dette
 * vaert en knapp som loey. Se `Menu.tsx`.
 */

export interface Medlem {
  readonly id: string;
  readonly navn: string;
  readonly initialer: string;
  readonly rolle: Session["member"]["role"];
  readonly teamId: string | null;
}

export interface Lag {
  readonly id: string;
  readonly navn: string;
}

export async function hentMedlemmer(produksjonId: string): Promise<readonly Medlem[]> {
  if (supabase === null || !erEkteProduksjon(produksjonId)) return [];
  const { data, error } = await supabase
    .from("production_member")
    .select("id, display_name, initials, role, unit_id")
    .eq("production_id", produksjonId)
    .order("display_name", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]),
    navn: String(r["display_name"]),
    initialer: String(r["initials"]),
    rolle: String(r["role"]) as Session["member"]["role"],
    teamId: r["unit_id"] == null ? null : String(r["unit_id"]),
  }));
}

export async function hentLag(produksjonId: string): Promise<readonly Lag[]> {
  if (supabase === null || !erEkteProduksjon(produksjonId)) return [];
  const { data, error } = await supabase
    .from("unit")
    .select("id, name")
    .eq("production_id", produksjonId)
    .order("name", { ascending: true });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]), navn: String(r["name"]),
  }));
}

/**
 * Blir den personen, paa det teamet.
 *
 * Teamet skrives til BASEN og ikke bare lokalt. Det maa det: policyene
 * spoer `production_member.unit_id` om du faar skrive i teamets oekt, og
 * nettflata leser det samme feltet. Et team som bare fantes paa telefonen
 * ville gitt to flater som sa ulike ting.
 *
 * Laster siden paa nytt, som rollebytteren paa nettflata. Oekta, den lokale
 * basen og takten leses alle ved oppstart, og aa vekke dem enkeltvis er
 * flere steder aa ta feil.
 */
export async function blirMedlem(m: Medlem, teamId: string | null): Promise<void> {
  const naa = hentOekt();
  if (supabase !== null && teamId !== m.teamId) {
    await supabase.from("production_member")
      .update({ unit_id: teamId }).eq("id", m.id);
  }
  settOekt({
    ...naa,
    member: {
      id: m.id, displayName: m.navn, initials: m.initialer, role: m.rolle,
    },
    unitId: teamId ?? "",
  });
  location.reload();
}
